import React, { ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { Box, Chip, SxProps, Tooltip } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';
import { PaletteKey } from '@europrocurement/flexy-components/assets';
import {
    ComponentColorResult,
    computeComponentColors,
    resolveColorMain,
} from '../../../hooks/useThemeContrastColor';

export type InformationChipProps = {
    label: ReactNode;
    tooltipTitle?: ReactNode;
    color: Partial<Record<PaletteKey, ComponentColorResult>>;
    icon?: IconProp;
    sx?: SxProps;
    sxIcon?: SxProps;
};

export const InformationChip: React.FunctionComponent<InformationChipProps> = function ({
    label,
    tooltipTitle,
    icon,
    color = 'primary',
    sx = undefined,
    sxIcon = undefined,
}) {
    const { palette } = useL2DTheme();
    const token =
        palette.chip?.[color] ??
        computeComponentColors(resolveColorMain(color as string, palette), palette.mode);
    const { glassyStyle, glassyStyleOnHover, textColor, textColorOnHover } = token;

    const [chipEl, setChipEl] = useState<HTMLDivElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const chipRef = useCallback((el: HTMLDivElement | null) => setChipEl(el), []);

    useLayoutEffect(() => {
        if (!chipEl) return undefined;

        const labelEl = chipEl.querySelector('.MuiChip-label') as HTMLElement | null;

        if (!labelEl) return undefined;

        const check = () => setIsOverflowing(labelEl.scrollWidth > labelEl.clientWidth);
        check();

        if (typeof ResizeObserver === 'undefined') return undefined;

        const ro = new ResizeObserver(check);
        ro.observe(labelEl);

        return () => ro.disconnect();
    }, [chipEl]);

    const chipBorderRadius = (sx as { borderRadius?: string | number })?.borderRadius ?? '8px';

    const chip = (
        <Chip
            ref={chipRef}
            data-testid="InformationChip-test-id"
            label={label}
            {...(icon && {
                icon: (
                    <FaOptionIcon
                        data-testid="InformationChip-icon-test-id"
                        icon={icon}
                        sx={{
                            fontSize: '0.7em',
                            paddingLeft: '7px',
                            display: 'inline-block',
                            ...sxIcon,
                        }}
                    />
                ),
            })}
            sx={{
                fontFamily: 'Inter',
                fontWeight: '400',
                fontSize: '14px',
                borderRadius: '8px',
                ...glassyStyle,
                color: textColor,
                padding: '16px 7px',
                width: 'max-content',
                maxWidth: '100%',
                flexShrink: 0,
                overflow: 'hidden',
                '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'unset',
                    ...(isOverflowing && {
                        maskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
                    }),
                },
                transition: 'all ease-in-out 300ms',
                '&:hover': {
                    ...glassyStyleOnHover,
                    color: textColorOnHover,
                },
                ...sx,
                pointerEvents: 'none',
            }}
        />
    );

    const wrapper = (
        <Box
            sx={{
                display: 'inline-flex',
                maxWidth: '100%',
                alignSelf: 'flex-start',
                overflow: 'hidden',
                cursor: 'default',
                borderRadius: chipBorderRadius,
            }}
        >
            {chip}
        </Box>
    );

    if (isOverflowing) {
        return <Tooltip title={tooltipTitle ?? label}>{wrapper}</Tooltip>;
    }

    return wrapper;
};
